/**
 * 通用员工选择器
 *
 * 用于项目中所有需要筛选/选择员工的场景。组件内部：
 * - 可见员工范围与权限：与员工列表一致，依赖权限点 staff.list.view（无权限时不展示可选数据）
 * - 数据源：GET /api/admin/users，支持关键词、状态筛选与分页
 *
 * 参数冗余、默认合适，便于上层直接使用。
 */
import { listAdminUsers } from '@/services/admin/user';
import { useAccess } from '@umijs/max';
import type { SelectProps } from 'antd';
import { Select, Spin } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface StaffSelectorProps
  extends Omit<SelectProps, 'options' | 'loading' | 'onSearch' | 'filterOption'> {
  /** 是否多选，默认 false */
  multiple?: boolean;
  /** 员工状态筛选：active / inactive，不传则不过滤 */
  status?: 'active' | 'inactive' | null;
  /** 请求每页条数（默认 50） */
  pageSize?: number;
  /** 无权限时是否隐藏选择器（默认 false，显示为禁用空选项） */
  hideWhenNoAccess?: boolean;
  /** 按角色编码过滤（只展示拥有该 code 角色的用户，如 opreation=运营专员） */
  roleCode?: string | null;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

const StaffSelector: React.FC<StaffSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择员工',
  multiple = false,
  status = 'active',
  pageSize = DEFAULT_PAGE_SIZE,
  hideWhenNoAccess = false,
  roleCode = null,
  disabled,
  allowClear = true,
  showSearch = true,
  optionFilterProp = 'label',
  ...rest
}) => {
  const access = useAccess();
  const canAccess = access.can('staff.list.view');

  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const debouncedKeyword = useDebounce(searchKeyword, SEARCH_DEBOUNCE_MS);
  const openRef = useRef(false);

  const fetchStaff = useCallback(
    async (keyword: string, append: boolean) => {
      if (!canAccess) return;
      setLoading(true);
      try {
        const res = await listAdminUsers({
          current: 1,
          pageSize: roleCode ? 200 : pageSize,
          keyword: keyword || undefined,
          status: status ?? undefined,
        });
        if (res.code === 0 && res.data?.list) {
          let raw = res.data.list as API.AdminUserResponse[];
          if (roleCode) {
            raw = raw.filter(
              (u) => u.roles?.some((r) => r.code === roleCode),
            );
          }
          const list = raw.map((u) => ({
            value: u.id,
            label: u.nickname?.trim() || u.username || u.id,
          }));
          setOptions((prev) => (append ? [...prev, ...list] : list));
        } else {
          if (!append) setOptions([]);
        }
      } catch {
        if (!append) setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [canAccess, pageSize, status, roleCode],
  );

  // 下拉已打开时，搜索关键词变化则重新拉取
  useEffect(() => {
    if (!canAccess || !openRef.current) return;
    fetchStaff(debouncedKeyword, false);
  }, [debouncedKeyword, canAccess, fetchStaff]);

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      openRef.current = open;
      if (open && canAccess && !loading) {
        fetchStaff(debouncedKeyword, false);
      }
    },
    [canAccess, loading, debouncedKeyword, fetchStaff],
  );

  if (hideWhenNoAccess && !canAccess) {
    return null;
  }

  const effectiveDisabled = disabled || !canAccess;

  return (
    <Select
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      mode={multiple ? 'multiple' : undefined}
      allowClear={allowClear}
      showSearch={showSearch}
      optionFilterProp={optionFilterProp}
      filterOption={false}
      onSearch={setSearchKeyword}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : '暂无员工数据'}
      options={options}
      disabled={effectiveDisabled}
      onOpenChange={handleDropdownVisibleChange}
      {...rest}
    />
  );
};

export default StaffSelector;
